import { Fragment, useEffect, useRef, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import RemoveIcon from "@mui/icons-material/Remove";
import AddOrEmptyTile from "../../components/AddOrEmptyTile";
import EmptySectionState from "../../components/EmptySectionState";
import { StyledLink } from "../../components/CustomMUI";
import { StandoutIcon } from "../../components/icons";
import PosterTile from "../../components/PosterTile";
import { useDetailNavigation } from "../../hooks/useDetailNavigation";
import { useStandoutMutations, useStandouts } from "../../hooks/useCustomQuery";
import Carousel from "../../components/Carousel";
import StandoutPickerDialog from "../standouts/StandoutPickerDialog";
import useUrlIntent from "../../hooks/useUrlIntent";
import StandoutAddFlow from "../standouts/StandoutAddFlow";
import PlaceholderFade from "../../components/PlaceholderFade";

// Posters are the same size as the top 5 card's: with page width P that's
// (P - 8) / 3 - 16 on xs and (P - 4) / 6 - 20 on sm+ (a grid item inside a
// 12px-padded card). The carousel row bleeds past the page gutter on the left
// and pads it back on the right, so its content box is P + gutter (16px xs,
// 24px sm/md) and P itself at lg, where the bleed is off.
export const SLOT_SX = {
  width: {
    xs: "calc((100% - 24px) / 3 - 16px)",
    sm: "calc((100% - 28px) / 6 - 20px)",
    lg: "calc((100% - 4px) / 6 - 20px)",
  },
};

// The backend sends at most this many (PROFILE_ROW_LIMIT); beyond it the row
// ends with a "See all" card
const ROW_LIMIT = 10;

// "3 standouts from 40 {unit} finished". TV standouts are seasons, so the
// "from N" count is too
const UNITS = {
  all: ["thing", "things"],
  movie: ["movie", "movies"],
  tvshow: ["season", "seasons"],
  book: ["book", "books"],
};
// For the owner's empty-state prompt
const PROMPT_NOUNS = { movie: "movies", tvshow: "TV seasons", book: "books" };

const INFO = (
  <Typography variant="body2">
    Spotlight the things that stayed with you this year with standouts
  </Typography>
);

/**
 * "{year} standouts" on the profile: this year's standouts for one content type
 * (or every type, for "all") in the same carousel as the collections row, with
 * an add tile for the owner (or, when empty, a muted empty state with an "Add a
 * standout" prompt). "See all" opens the year's (mixed) standouts collection
 * filtered to this type (unfiltered for "all"); links underneath open other
 * years' the same way. Visitors see last year's until this year has some.
 */
export default function ProfileStandouts({
  username,
  contentType,
  isOwnProfile,
}) {
  const { data, isLoading, isPlaceholderData } = useStandouts(
    username,
    contentType,
  );
  const { removeStandout } = useStandoutMutations();
  const { openContent, openList } = useDetailNavigation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [flowTarget, setFlowTarget] = useState(null); // { contentId, title }
  const sectionRef = useRef(null);
  const reduceMotion = useReducedMotion();
  // ?add=standout (e.g. Getting started's standout step): scroll here first,
  // then open the picker over the section, so closing it reveals the result
  const [isPickerRequested, setIsPickerRequested] = useState(false);
  useUrlIntent("standout", () => setIsPickerRequested(true), {
    enabled: isOwnProfile,
  });
  useEffect(() => {
    if (!isPickerRequested || isLoading || !sectionRef.current)
      return undefined;
    sectionRef.current.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
    // Lets the scroll settle before the dialog covers it
    const timer = setTimeout(
      () => {
        setIsPickerRequested(false);
        setPickerOpen(true);
      },
      reduceMotion ? 0 : 400,
    );
    return () => clearTimeout(timer);
  }, [isPickerRequested, isLoading, reduceMotion]);

  // The standout just added pops in when it lands in the row; cleared after,
  // so switching type and back doesn't replay it
  const [justAddedId, setJustAddedId] = useState(null);
  useEffect(() => {
    if (!justAddedId) return undefined;
    const timer = setTimeout(() => setJustAddedId(null), 1500);
    return () => clearTimeout(timer);
  }, [justAddedId]);

  if (isLoading) {
    return (
      <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
    );
  }
  if (!data) return null;

  const { year, list_id, items, standout_count, finished_count, past_years } =
    data;

  // Opens a year's standouts in the collection dialog, filtered to the
  // selected type (unfiltered for "all")
  const openYear = (listId, listYear) =>
    openList(
      { list_id: listId, title: `${listYear} Standouts` },
      { type: contentType === "all" ? undefined : contentType },
    );
  const [singular, plural] = UNITS[contentType] ?? ["item", "items"];
  const subtitle =
    `${standout_count} ${standout_count === 1 ? "standout" : "standouts"} from ` +
    `${finished_count} ${finished_count === 1 ? singular : plural} finished`;

  // Owners get an add tile at the end of a non-empty row; an empty row shows
  // the empty state (with its own add prompt) instead
  const rowItems =
    isOwnProfile && items.length > 0 ? [...items, { addTile: true }] : items;

  const emptyState = isOwnProfile ? (
    <EmptySectionState
      message={`Nothing here yet — spotlight the ${PROMPT_NOUNS[contentType] ?? "things"} that stood out to you this year`}
      actionLabel="Add a standout"
      onAction={() => setPickerOpen(true)}
    />
  ) : (
    <EmptySectionState
      message={`@${username} hasn't picked any standouts yet this year`}
    />
  );

  const renderItem = (item) =>
    item.content_id === justAddedId && !reduceMotion ? (
      <Box
        component={motion.div}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 20 }}
      >
        {renderTile(item)}
      </Box>
    ) : (
      renderTile(item)
    );

  const renderTile = (item) =>
    item.addTile !== undefined ? (
      // Matches the "See all" card that can follow it
      <AddOrEmptyTile onClick={() => setPickerOpen(true)} matchSeeAll />
    ) : (
      <PosterTile
        contentId={item.content_id}
        contentType={item.content_type}
        contentTitle={item.title}
        imageUrl={item.image_url}
        overlay={true}
        // Your own standouts show no status border
        showStatus={isOwnProfile ? null : "full"}
        // The All view mixes types, so mark each poster's type
        showType={contentType === "all"}
        onClick={() =>
          openContent({
            contentId: item.content_id,
            contentType: item.content_type,
            title: item.title,
          })
        }
        listActions={
          isOwnProfile
            ? [
                {
                  label: "Remove",
                  value: "remove",
                  icon: <RemoveIcon fontSize="inherit" />,
                  onClick: () => removeStandout(item.content_id),
                },
              ]
            : []
        }
      />
    );

  const handlePick = (contentId, title) => {
    setPickerOpen(false);
    setFlowTarget({ contentId, title });
  };

  return (
    // scrollMarginTop clears the sticky content-type pills
    <Box ref={sectionRef} sx={{ scrollMarginTop: 72 }}>
      <PlaceholderFade active={isPlaceholderData}>
        <Carousel
          title={`${year} standouts`}
          description={subtitle}
          info={INFO}
          items={rowItems}
          renderItem={renderItem}
          getItemKey={(item) => (item.addTile ? "add" : item.content_id)}
          itemSx={SLOT_SX}
          // Every standout has PosterTile's long press (Remove for the owner, the
          // status overlay for visitors), so hover only; the add tile has no scale
          itemScale={(item) => (item.addTile ? "none" : "hover")}
          onSeeMore={list_id ? () => openYear(list_id, year) : undefined}
          emptyState={emptyState}
          titleIcon={<StandoutIcon color="primary" />}
          showSeeMoreCard={standout_count > ROW_LIMIT}
        />

        {past_years.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Past standouts:{" "}
            {past_years.map(
              ({ year: pastYear, list_id: pastListId }, index) => (
                <Fragment key={pastYear}>
                  {index > 0 && " · "}
                  <StyledLink
                    component="button"
                    variant="body2"
                    onClick={() => openYear(pastListId, pastYear)}
                    sx={{ verticalAlign: "baseline" }}
                  >
                    {pastYear}
                  </StyledLink>
                </Fragment>
              ),
            )}
          </Typography>
        )}

        {isOwnProfile && (
          <>
            <StandoutPickerDialog
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              contentType={contentType}
              onPick={handlePick}
            />
            <StandoutAddFlow
              contentId={flowTarget?.contentId ?? null}
              title={flowTarget?.title}
              onClose={() => setFlowTarget(null)}
              onAdded={setJustAddedId}
            />
          </>
        )}
      </PlaceholderFade>
    </Box>
  );
}
